import * as classNames from 'classnames'
import * as React from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import { config } from '../../../../../common/config'
import BasicForm from '../../basicForm'

import * as styles from './signIn.module.css'

interface IProps {
  showReCAPTCHA: boolean
  show2FA: boolean
  showUsernameMissing: boolean
  showPasswordMissing: boolean
  showServerError: boolean
  showLoading: boolean
  data: any
  passwordErrorMsg: string
  twoFAErrorMsg: string
  openModalData: any
  onSubmit: any
  onInputChange: any
  onReCAPTCHAChange: any
  recaptchaKey: string
  onClickSignUp: any
}

const SignInModal = ({
  showReCAPTCHA,
  show2FA,
  showUsernameMissing,
  showPasswordMissing,
  showServerError,
  showLoading,
  data,
  passwordErrorMsg,
  twoFAErrorMsg,
  openModalData,
  onSubmit,
  onInputChange,
  onReCAPTCHAChange,
  recaptchaKey,
  onClickSignUp
}: IProps) => {
  const { welcomeMsg, welcomeMsgDescription } = openModalData
  const logoAssetsPath = `${config.assets.PUBLIC_PATH}/images/envato_white.svg`
  const ssoPath = config.sso.SERVER_HOST
  const modalClassNames = classNames('fds-c-modal', '-width-s', {
    'is-disabled-loading': showLoading
  })

  // apply invalid status to form when either is missing or wrong details
  const usernameInputStatus =
    showUsernameMissing || passwordErrorMsg ? 'invalid' : null
  const passwordInputStatus =
    showPasswordMissing || passwordErrorMsg ? 'invalid' : null
  const twoFAInputStats = twoFAErrorMsg ? 'invalid' : null

  return (
    <div className={modalClassNames}>
      <header className="fds-c-modal__header fds-u-text-align-center fds-c-modal__section -background-dark">
        <div className="fds-u-py1">
          <img alt="Envato" width="130" src={logoAssetsPath} />
        </div>
      </header>

      <div className="fds-c-modal__section -radius-bottom">
        {welcomeMsg && (
          <h2 className="fds-c-text-heading fds-u-text-align-center -size-s fds-u-mb0 fds-u-pt2 fds-u-pb2">
            {welcomeMsg}
          </h2>
        )}
        {welcomeMsgDescription && (
          <p className="fds-c-text-body fds-u-text-align-center -size-s fds-u-mb0 fds-u-pb2">
            {welcomeMsgDescription}
          </p>
        )}
        <BasicForm
          method="post"
          action="javascript:void(0);"
          acceptCharset="UTF-8"
          onSubmit={onSubmit}
          className="fds-c-form -layout-vertical-inline"
          noValidate
        >
          <div className="fds-c-form__group fds-u-text-align-center">
            <div className={styles.formLabelInlineGroup}>
              <div className={styles.formLabel}>
                <span>Username</span>
              </div>
              <div>
                <a
                  className="fds-c-text-link -decoration-none"
                  href={`${ssoPath}/recover_usernames`}
                >
                  Remind me
                </a>
              </div>
            </div>
            <div className="fds-c-form__input">
              <input
                autoFocus
                tabIndex={0}
                data-status={usernameInputStatus}
                defaultValue={data.username}
                onChange={onInputChange('username')}
                name="username"
                className="fds-c-form-input -type-string"
                size={50}
                type="text"
                required
              />
            </div>
            {showUsernameMissing && (
              <div id="missing-username" className={styles.formResponse}>
                Username is required
              </div>
            )}
          </div>

          <div className="fds-c-form__group fds-u-text-align-center">
            <div className={styles.formLabelInlineGroup}>
              <div className={styles.formLabel}>
                <span>Password</span>
              </div>
              <div>
                <a
                  tabIndex={-1}
                  className="fds-c-text-link -decoration-none"
                  href={`${ssoPath}/reset_password`}
                >
                  Reset
                </a>
              </div>
            </div>
            <div className="fds-c-form__input">
              <input
                data-status={passwordInputStatus}
                defaultValue={data.password}
                onChange={onInputChange('password')}
                name="password"
                className="fds-c-form-input -type-string"
                size={50}
                type="password"
                required
              />
            </div>
            {showPasswordMissing && (
              <div id="missing-password" className={styles.formResponse}>
                Password is required
              </div>
            )}
            {passwordErrorMsg && (
              <div id="password-error" className="fds-c-form__response">
                {passwordErrorMsg}
              </div>
            )}
          </div>

          {showReCAPTCHA && (
            <div id="recaptcha" className="fds-c-form__group">
              <div className={styles.captcha}>
                <ReCAPTCHA
                  sitekey={recaptchaKey}
                  onChange={onReCAPTCHAChange}
                />
              </div>
            </div>
          )}

          {show2FA && (
            <div id="two-fa" className="fds-c-form__group">
              <div className={styles.formLabelInlineGroup}>
                <div className={styles.formLabel}>Authentication Code</div>
              </div>

              <div className="fds-c-form__input fds-u-text-align-center">
                <input
                  data-status={twoFAInputStats}
                  defaultValue={data.authentication_code}
                  onChange={onInputChange('authentication_code')}
                  name="twoFactor"
                  className="fds-c-form-input -type-string"
                  size={50}
                  type="text"
                />
              </div>
              {twoFAErrorMsg && (
                <div className="fds-c-form__response fds-u-text-align-center">
                  {twoFAErrorMsg}
                </div>
              )}
            </div>
          )}

          {showServerError && (
            <div
              id="server-error"
              className="fds-c-form__group  fds-u-text-align-center"
            >
              <div className="fds-c-form__response">
                Whoops! Something went wrong.
                <br />
                Please try again or sign in via this{' '}
                <a href={`${ssoPath}/`}>link</a>.
              </div>
            </div>
          )}

          <p className="fds-u-text-align-center">
            <button
              disabled={showLoading}
              type="submit"
              className="fds-c-button--3d -color-primary -size-m"
            >
              Sign in to your account
            </button>
          </p>

          <p className="fds-u-text-align-center fds-u-mt2 fds-u-mb3">
            New here?{' '}
            <a
              data-test-selector="sign-up-modal-button"
              className="fds-c-text-link -decoration-none"
              href={`${ssoPath}/sign_up`}
              onClick={onClickSignUp}
            >
              Create an Envato account
            </a>
          </p>
        </BasicForm>
      </div>
    </div>
  )
}

export default SignInModal
